import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAdminManagement } from '../hooks/useAdminManagement';
import AddAdminModal from './AddAdminModal';
import AddAdminProgressPopup from './AddAdminProgressPopup';
import DeleteAdminProgressPopup from './DeleteAdminProgressPopup';
import DisplayNameMismatchModal from './DisplayNameMismatchModal';
import DeleteAdminConfirmation from './DeleteAdminConfirmation';

const AdminManagement = ({ subscriptionData, onRefresh, showMessage, isSuspended = false }) => {
const [showAddAdminModal, setShowAddAdminModal] = useState(false);
const [addingAdminName, setAddingAdminName] = useState(null);
const [deletingAdminName, setDeletingAdminName] = useState(null);
const [adminsData, setAdminsData] = useState(null);
const [searchQuery, setSearchQuery] = useState('');
const [displayNameMismatch, setDisplayNameMismatch] = useState(null);
const [prefillDisplayName, setPrefillDisplayName] = useState(null);
const [deleteModalState, setDeleteModalState] = useState({ 
  show: false, 
  adminId: null, 
  adminName: null 
});
  
const { getUserEmail } = useAuth();
const { loading, error, addAdminProgress, deleteAdminProgress, getAdmins, addAdmin, deleteAdmin, currentAdminId } = useAdminManagement(
  subscriptionData,
  null // Remove success messages
);

  // Load admins data
  const loadAdmins = async () => {
    try {
      const data = await getAdmins();
      setAdminsData(data);
    } catch (err) {
      showMessage?.(`Failed to load admins: ${err.message}`, 'error');
    }
  };

  // Load admins when component mounts or subscription data changes
  useEffect(() => {
    if (subscriptionData && currentAdminId) {
      loadAdmins();
    }
  }, [subscriptionData, currentAdminId]);

  // Handle adding new admin
  const handleAddAdmin = async (email, fullName) => {
    // Check if user with this email already exists
    const existingUser = subscriptionData?.users?.find(u => 
      (u.email || u.Email)?.toLowerCase() === email.toLowerCase()
    );
    
    if (existingUser) {
      const existingUserName = existingUser.fullName || existingUser.FullName;
      if (existingUserName && existingUserName.trim() !== fullName.trim()) {
        // Show the mismatch modal instead of a simple error
        setDisplayNameMismatch({
          email: email,
          existingDisplayName: existingUserName
        });
        return;
      }
    }
    
    // Store admin name for progress display
    setAddingAdminName(fullName);
    
    try {
      await addAdmin(email, fullName);
      // If successful, close the modal and refresh data
      setShowAddAdminModal(false);
      setPrefillDisplayName(null);
      loadAdmins();
      onRefresh?.();
    } catch (err) {
      showMessage?.(err.message, 'error');
      // Don't close modal on error so user can retry
    } finally {
      // Clear the adding admin name after a delay
      setTimeout(() => setAddingAdminName(null), 1500);
    }
  };

  const handleUseExistingName = async (existingName) => {
    // Close the mismatch modal
    setDisplayNameMismatch(null);
    
    // Get the email from the mismatch state
    const email = displayNameMismatch.email;
    
    // Proceed with adding the admin using the existing name
    setAddingAdminName(existingName);
    
    try {
      await addAdmin(email, existingName);
      // If successful, close the add admin modal and refresh data
      setShowAddAdminModal(false);
      setPrefillDisplayName(null);
      loadAdmins();
      onRefresh?.();
    } catch (err) {
      showMessage?.(err.message, 'error');
      // On error, prefill the name so user can see what was attempted
      setPrefillDisplayName(existingName);
    } finally {
      // Clear the adding admin name after a delay
      setTimeout(() => setAddingAdminName(null), 1500);
    }
  };

  const handleCloseMismatchModal = () => {
    setDisplayNameMismatch(null);
  };


  // Handle deleting admin
  const handleDeleteAdmin = async (adminToDelete) => {
    const currentUserEmail = getUserEmail();
    
    // Double-check self-deletion prevention
    if (adminToDelete.email === currentUserEmail || adminToDelete.Email === currentUserEmail) {
      showMessage?.('You cannot delete yourself', 'error');
      return;
    }

    // Also prevent deleting the subscription's primary/beneficiary admin
    const origId = subscriptionData?.createdByAdminID || subscriptionData?.CreatedByAdminID ||
                   subscriptionData?.ownerAdminID || subscriptionData?.OwnerAdminID ||
                   subscriptionData?.createdBy || subscriptionData?.CreatedBy || null;
    const origEmail = subscriptionData?.createdByEmail || subscriptionData?.CreatedByEmail ||
                      subscriptionData?.ownerAdminEmail || subscriptionData?.OwnerAdminEmail ||
                      subscriptionData?.createdByUser || subscriptionData?.CreatedByUser ||
                      subscriptionData?.BeneficiaryEmail || subscriptionData?.beneficiaryEmail || null;

    const adminIdToDelete = adminToDelete.adminID || adminToDelete.AdminID;
    const adminEmailToDelete = adminToDelete.email || adminToDelete.Email || '';

    const normAdminIdToDelete = adminIdToDelete != null ? String(adminIdToDelete) : null;
    const normOrigId = origId != null ? String(origId) : null;
    const normAdminEmailToDelete = adminEmailToDelete.trim().toLowerCase();
    const normOrigEmail = origEmail ? String(origEmail).trim().toLowerCase() : null;

    if ((normOrigId && normAdminIdToDelete === normOrigId) || (normOrigEmail && normAdminEmailToDelete === normOrigEmail)) {
      showMessage?.('You cannot delete the primary subscription administrator (beneficiary).', 'error');
      return;
    }

    // Check if this is the last admin
    const adminCount = adminsData?.admins?.length || subscriptionData?.admins?.length || 0;
    if (adminCount <= 1) {
      showMessage?.('Cannot delete the last admin. Each subscription must have at least one admin.', 'error');
      return;
    }

    const adminName = adminToDelete.fullName || adminToDelete.FullName || adminToDelete.email || adminToDelete.Email;
    const adminId = adminToDelete.adminID || adminToDelete.AdminID;
    
    // Show the delete confirmation modal
    setDeleteModalState({
      show: true,
      adminId: adminId,
      adminName: adminName
    });
  };

  const handleConfirmDelete = async () => {
    const { adminId, adminName } = deleteModalState;
    
    // Store admin name for progress display
    setDeletingAdminName(adminName);
    
    // Close modal
    setDeleteModalState({ show: false, adminId: null, adminName: null });

    try {
      await deleteAdmin(adminId);
      // Refresh data after successful deletion
      loadAdmins();
      onRefresh?.();
    } catch (err) {
      showMessage?.(`Failed to delete admin: ${err.message}`, 'error');
    } finally {
      // Clear the deleting admin name after a delay
      setTimeout(() => setDeletingAdminName(null), 1500);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalState({ show: false, adminId: null, adminName: null });
  };

  // Use the more detailed admins data if available, otherwise fall back to subscription data
  const displayAdmins = adminsData?.admins || subscriptionData?.admins || [];
  // Determine the subscription creator/original admin (many backends use different names)
  const originalAdminId = subscriptionData?.createdByAdminID || subscriptionData?.CreatedByAdminID ||
                          subscriptionData?.ownerAdminID || subscriptionData?.OwnerAdminID ||
                          subscriptionData?.createdBy || subscriptionData?.CreatedBy || null;
  // Also check the subscription Beneficiary Email (primary owner) - common field names
  const originalAdminEmail = subscriptionData?.createdByEmail || subscriptionData?.CreatedByEmail ||
                             subscriptionData?.ownerAdminEmail || subscriptionData?.OwnerAdminEmail ||
                             subscriptionData?.createdByUser || subscriptionData?.CreatedByUser ||
                             subscriptionData?.BeneficiaryEmail || subscriptionData?.beneficiaryEmail || null;
  const filteredAdmins = (displayAdmins || []).filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const email = (a.email || a.Email || '').toLowerCase();
    const name = (a.fullName || a.FullName || '').toLowerCase();
    return email.includes(q) || name.includes(q);
  });
  const currentUserEmail = getUserEmail();

  if (!subscriptionData) {
    return null;
  }

  return (
    <div className="section">
      {/* Subscription Suspended Warning */}
      {isSuspended && (
        <div style={{ 
          background: '#fff3cd', 
          border: '2px solid #ffc107', 
          padding: '1.5rem', 
          marginBottom: '1.5rem',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16" style={{ color: '#856404', flexShrink: 0 }}>
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>Subscription Suspended</h3>
            <p style={{ margin: 0, color: '#856404' }}>
              Your subscription is currently suspended. All management functions are disabled and users will be unable to access their reports until this is resolved. 
              Please contact Microsoft support to resolve billing or payment issues.
            </p>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Administrator Management</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search admins by name or email"
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #dee2e6', borderRadius: '6px', minWidth: '260px', fontSize: '14px', height: '38px', boxSizing: 'border-box' }}
            disabled={isSuspended}
          />
          <button 
            className="btn btn-primary" 
            onClick={() => setShowAddAdminModal(true)}
            disabled={loading || isSuspended}
            style={{ height: '38px', padding: '0.5rem 0.75rem', boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}
            title={isSuspended ? 'Disabled - Subscription Suspended' : 'Add Admin'}
          >
            + Add Admin
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="alert alert-error" style={{ background: '#fee', border: '1px solid #fcc', padding: '1rem', marginBottom: '1rem' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Admins table */}
      <div className="admins-list">
         <h3>Current Administrators ({filteredAdmins.length})</h3>
         {displayAdmins.length === 0 ? (
          <p>No administrators found.</p>
         ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Full Name</th>
                  <th style={{ width: '35%' }}>Email</th>
                  <th style={{ width: '15%' }}>Reports</th>
                  <th style={{ width: '20%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAdmins.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                      No admins match your search.
                    </td>
                  </tr>
                ) : (
                  filteredAdmins.map((admin) => {
                    const isCurrentUser = (admin.email || admin.Email) === currentUserEmail;
                    const adminEmail = admin.email || admin.Email;
                    const adminName = admin.fullName || admin.FullName || 'No Name Provided';
                    const adminId = admin.adminID || admin.AdminID;
                    const uploadedCount = admin.uploadedReportsCount || admin.UploadedReportsCount || 0;
                    
                    // Normalize IDs and emails for reliable comparison (handle types/casing)
                    const normAdminId = adminId != null ? String(adminId) : null;
                    const normOriginalAdminId = originalAdminId != null ? String(originalAdminId) : null;
                    const normAdminEmail = adminEmail ? adminEmail.trim().toLowerCase() : null;
                    const normOriginalAdminEmail = originalAdminEmail ? originalAdminEmail.trim().toLowerCase() : null;

                    const isOriginalAdmin = (normOriginalAdminId && normAdminId === normOriginalAdminId) ||
                                            (normOriginalAdminEmail && normAdminEmail === normOriginalAdminEmail);

                    return (
                      <tr 
                        key={adminId} 
                        className={isCurrentUser ? 'current-user-row' : ''}
                        style={{ background: isCurrentUser ? '#e8f5e8' : undefined }}
                      >
                        <td style={{ fontWeight: '600' }}>
                          {adminName}
                          {isCurrentUser && <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#28a745' }}>(You)</span>}
                        </td>
                        <td>{adminEmail}</td>
                        <td>{uploadedCount}</td>
                        <td>
                          {!isOriginalAdmin && !isCurrentUser && displayAdmins.length > 1 ? (
                            <button 
                              className="btn btn-danger"
                              onClick={() => handleDeleteAdmin(admin)}
                              disabled={loading || isSuspended}
                              style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                              title={isSuspended ? 'Disabled - Subscription Suspended' : 'Delete Admin'}
                            >
                              Delete
                            </button>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: '0.8rem', fontStyle: 'italic' }}>
                              {isCurrentUser ? 'Cannot delete yourself' : isOriginalAdmin ? 'Primary admin' : 'Last admin'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Loading indicator */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <div className="loading" style={{ display: 'inline-block' }}></div>
          <span style={{ marginLeft: '0.5rem' }}>Processing...</span>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <AddAdminModal
          onClose={() => {
            setShowAddAdminModal(false);
            setPrefillDisplayName(null);
          }}
          onAddAdmin={handleAddAdmin}
          loading={loading}
          prefillDisplayName={prefillDisplayName}
        />
      )}

      {/* Display Name Mismatch Modal */}
      {displayNameMismatch && (
        <DisplayNameMismatchModal
          email={displayNameMismatch.email}
          existingDisplayName={displayNameMismatch.existingDisplayName}
          onClose={handleCloseMismatchModal}
          onUseExistingName={handleUseExistingName}
        />
      )}

      {/* Add Admin Progress Popup */}
      {addAdminProgress && (
        <AddAdminProgressPopup
          progress={addAdminProgress}
          adminName={addingAdminName}
        />
      )}

      {/* Delete Admin Progress Popup */}
      {deleteAdminProgress && (
        <DeleteAdminProgressPopup
          progress={deleteAdminProgress}
          adminName={deletingAdminName}
        />
      )}

      {/* Delete Admin Confirmation Modal */}
      {deleteModalState.show && (
        <DeleteAdminConfirmation
          adminName={deleteModalState.adminName}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}

    </div>
  );
};

export default AdminManagement;